// The metric selector: a fixed-label trigger opening a checkbox list of what the question offers,
// led by the one governing row -- All answers on a chart, All metrics on a table. What the list
// holds and what a click does are worked out in the selector rules; this draws them.
//
// Spec: specs/stats/questions/summary_metrics/functional_requirements.md §6.1, §6.9

import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Checkbox } from 'primereact/checkbox'
import { ListBox } from 'primereact/listbox'
import { useMemo, useRef } from 'react'

import OverlayPanel from '@aytm/app/stats/components/overlay-panel/OverlayPanel'
import { applySelectorChange, calcSelectorOptions } from '@aytm/app/stats/page/question-views/mx/lib/summary/selector'

import style from '@aytm/app/stats/page/question-views/mx/components/summary/styles/MetricsDropdown.module.scss'

// The trigger names the control, not the state: the selection is visible in the chart or the table
// itself, and a label that grows with it pushes the icons around and truncates into nonsense.
const TRIGGER_LABEL = 'Metrics'

const MetricsDropdown = ({ metrics, selection, disabled, governsChart = true, onChange }) => {
  const ref = useRef(null)

  const options = useMemo(
    () => calcSelectorOptions({ metrics, selection, governsChart }),
    [metrics, selection, governsChart]
  )

  const value = options.filter(option => option.checked).map(option => option.key)

  // The list reports the whole new set; which row the reader touched is the difference between it
  // and the old one, and that is what the selector rules act on.
  const handleChange = event => {
    const next = new Set(event.value || [])
    const touched = options.map(option => option.key).find(key => next.has(key) !== value.includes(key))
    if (null == touched) return

    onChange(applySelectorChange({ metrics, selection, governsChart }, touched))
  }

  if (!metrics?.items?.length) return null

  if (disabled) return <div className={style.metrics}>{TRIGGER_LABEL}</div>

  return (
    <div className={style.metrics}>
      <OverlayPanel
        ref={ref}
        panelClassName={style.panel}
        button={
          <div className={style.button}>
            <span className={style.buttonText}>{TRIGGER_LABEL}</span>
            <span className={style.arrow} />
          </div>
        }
      >
        <ListBox
          multiple
          value={value}
          options={options}
          optionLabel="text"
          optionValue="key"
          optionDisabled="disabled"
          onChange={handleChange}
          itemTemplate={option => (
            <div className={classNames(style.item, { [style.divided]: option.divided })}>
              <Checkbox checked={option.checked} disabled={option.disabled} />
              <span className={style['item-text']}>{option.text}</span>
              {option.note ? <span className={style.note}>{option.note}</span> : null}
            </div>
          )}
        />
      </OverlayPanel>
    </div>
  )
}

MetricsDropdown.displayName = 'MetricsDropdown'

MetricsDropdown.propTypes = {
  metrics: PropTypes.shape({ items: PropTypes.array }),
  selection: PropTypes.shape({ keys: PropTypes.array, allAnswers: PropTypes.bool }),
  disabled: PropTypes.bool,
  // Whether this selector governs a chart. One that does not has no answer columns to hide, so it
  // offers no row for hiding them -- and nothing it lists is drawn anywhere, so marking the metrics
  // that cannot be plotted would mark the whole list and say nothing.
  governsChart: PropTypes.bool,
  onChange: PropTypes.func.isRequired
}

export default MetricsDropdown
